import { Alert, Box, Card, CardContent, IconButton, Skeleton, Tooltip, Typography } from '@mui/material'
import {
  DragIndicator as DragIndicatorIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

interface WidgetCardProps {
  title: string
  editMode: boolean
  loading: boolean
  error?: string | null
  onHide?: () => void
  children: React.ReactNode
}

/**
 * Рамка виджета дашборда: заголовок, ручка перетаскивания и кнопка
 * скрытия (в режиме редактирования), Skeleton при загрузке.
 */
export function WidgetCard({ title, editMode, loading, error, onHide, children }: WidgetCardProps) {
  const { t } = useTranslation()

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {editMode && (
          <Box
            className="widget-drag-handle"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.secondary' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        {editMode && onHide && (
          <Tooltip title={t('dashboard.widget.hide')}>
            <IconButton size="small" onClick={onHide}>
              <VisibilityOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <CardContent sx={{ pt: 0.5, flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
        {loading ? (
          <Box>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={48} sx={{ mt: 1 }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 0 }}>
            {error}
          </Alert>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )
}
